@let accessStatus = accessService.accessStatus | async;
@let activeLevel = layerStore.activeLevel | async;
@let isMapGroupAccessible =
  (mapColorFeatureList | isGroupAccessible: accessStatus) || !isPaidPlan;
@let isChartGroupAccessible =
  (chartFeatureList | isGroupAccessible: accessStatus) || !isPaidPlan;
@let isPlacesGroupAccessible =
  (placesFeatureList | isGroupAccessible: accessStatus) || !isPaidPlan;
@let isAnyGroupAccessible =
  isMapGroupAccessible || isChartGroupAccessible || isPlacesGroupAccessible;
@let userPlan = accessService.userPlan | async;

@if (
  (activeMenuGroup === MAPS_GROUP &&
    groupData.data.mapColor &&
    isMapGroupAccessible) ||
  (activeMenuGroup === CHARTS_GROUP &&
    groupData.data.charts &&
    isChartGroupAccessible) ||
  (activeMenuGroup === PLACES_GROUP &&
    groupData.data.places &&
    isPlacesGroupAccessible) ||
  (activeMenuGroup === ALL_DATA_GROUP &&
    (groupData.data.places ||
      groupData.data.charts ||
      groupData.data.mapColor) &&
    isAnyGroupAccessible)
) {
  <div
    [class.layer-opened]="isFeatureVisible"
    [class.filters-opened]="isFiltersOpened"
    class="layers-info-container"
  >
    <!-- The whole line is the control, so the arrow is a marker rather than a
         button of its own: one element instead of three, and the keyboard gets
         the header itself with the state it actually toggles. -->
    <div
      class="layers-info-title"
      role="button"
      tabindex="0"
      [attr.aria-expanded]="isFeatureVisible"
      (click)="toggleGroup()"
      (keydown.enter)="toggleGroup()"
      (keydown.space)="$event.preventDefault(); toggleGroup()"
    >
      <app-icon
        [class.opened-arrow-icon]="isFeatureVisible"
        class="arrow-right-icon title-arrow"
        svgIcon="arrow_right"
      >
      </app-icon>
      <span class="title-text">{{
        groupData.title | groupTitleFormatter: userPlan
      }}</span>
      @if (groupData.tooltip) {
        <app-feature-tooltip
          [tooltipText]="groupData.tooltip.text"
          [tooltipRouterLink]="groupData.tooltip.routerLink"
          (openTooltipModal)="handleTooltipModalOpen($event)"
        ></app-feature-tooltip>
      }
    </div>

    <!-- Every map-colour indicator in every group renders through one list:
         see LayerRowsComponent for why it is a row rather than a radio. -->
    @if (
      isFeatureVisible &&
      (activeMenuGroup === MAPS_GROUP || activeMenuGroup === ALL_DATA_GROUP) &&
      groupData.data.mapColor
    ) {
      <app-layer-rows
        class="layer-rows"
        [features]="groupData.data.mapColor!"
        [isPaidPlan]="isPaidPlan"
        [selectedScope]="groupData.scope ? selectedScope : null"
        [showScope]="!!groupData.scope && activeLevel !== STATE_LEVEL_LAYER"
        [disclaimer]="groupData.disclaimer ?? null"
        (scopeChange)="onScopeToggle($event)"
        (layerSelect)="onActiveLayerToggle($event)"
        (lockedFeatureClick)="
          handleInaccessibleFeatureClick(
            $event.featureConst,
            $event.featureName
          )
        "
        (openTooltipModal)="handleTooltipModalOpen($event)"
      ></app-layer-rows>
    }

    @if (
      isFeatureVisible &&
      (activeMenuGroup === CHARTS_GROUP ||
        activeMenuGroup === ALL_DATA_GROUP) &&
      groupData.data.charts
    ) {
      <div class="charts-container toggle-wrapper">
        @for (chart of groupData.data.charts; track chart.featureConst) {
          @let isChartAccessible =
            chart.featureConst | isFeatureAccessible: accessStatus;

          @if (
            (!isPaidPlan && !chart.isAdminOnly) ||
            (isPaidPlan && isChartAccessible)
          ) {
            <app-chart-block
              class="menu-list-row"
              [class.is-locked]="!isChartAccessible"
              [chart]="chart"
              [featureStatus]="groupData.featureStatus!"
              [toggle]="layerDataService.toggleMap.get(chart.featureConst)!"
              (changeSelectedYear)="onSelectedYearChange($event)"
              (click)="
                handleInaccessibleFeatureClick(
                  chart.featureConst,
                  chart.toggleTitle
                )
              "
              [disabled]="!isChartAccessible"
            ></app-chart-block>
          }
        }
      </div>
    }

    @if (
      isFeatureVisible &&
      (activeMenuGroup === PLACES_GROUP ||
        activeMenuGroup === ALL_DATA_GROUP) &&
      groupData.data.places
    ) {
      <div class="charts-container toggle-wrapper">
        @if (
          groupData.disclaimer &&
          layerStore.currentZoom! < groupData.disclaimer.visibleOnZoom?.min!
        ) {
          <app-feature-disclaimer
            [disclaimer]="groupData.disclaimer"
          ></app-feature-disclaimer>
        }

        <!--
          These four depend on the group, not on the place being rendered, so
          they sit outside the loop: one Map lookup per group instead of one
          per row.
        -->
        @let schoolCtrl = layerDataService.placesToggleMap.get(SCHOOLS_POI);
        @let oriCtrl = layerDataService.placesToggleMap.get(ORI_LAYER);
        @let isSchoolToggleActive =
          groupData.title === EDUCATION_GROUP_TITLE && schoolCtrl?.value;
        @let isOriToggleActive =
          groupData.title === CRIME_GROUP_TITLE && oriCtrl?.value;

        @for (
          feature of $any(groupData.data.places);
          track feature.featureConst
        ) {
          @let isPlaceAccessible =
            feature.featureConst | isFeatureAccessible: accessStatus;
          @let toShowFeature =
            (!isPaidPlan && !feature.isAdminOnly) ||
            (isPaidPlan && isPlaceAccessible);

          @if (
            feature.disclaimer &&
            layerStore.currentZoom! < feature.disclaimer.visibleOnZoom?.min! &&
            toShowFeature
          ) {
            <app-feature-disclaimer
              [disclaimer]="feature.disclaimer"
            ></app-feature-disclaimer>
          }

          @if (toShowFeature && groupData.title !== POI_GROUP_TITLE) {
            <app-slide-toggle
              class="menu-list-row"
              [class.is-locked]="!isPlaceAccessible"
              [formControl]="
                layerDataService.placesToggleMap.get(feature.featureConst)!
              "
              (change)="handlePointsToggleChange($event)"
              [disabled]="!isPlaceAccessible"
            >
              {{ $any(feature).featureName }}
              <app-feature-tooltip
                [tooltipText]="feature.tooltip.text"
                [isFeatureInaccessibleType]="!isPlaceAccessible"
                [headerText]="feature.tooltip.headerText"
                [featureConst]="feature.featureConst"
                [featureName]="feature.featureName || feature.toggleTitle"
                (openTooltipModal)="handleTooltipModalOpen($event)"
              ></app-feature-tooltip>
            </app-slide-toggle>
          }

          @if ($any(feature).toggleTitle && toShowFeature) {
            <app-chart-block
              class="menu-list-row"
              [class.is-locked]="!isPlaceAccessible"
              [chart]="$any(feature)"
              [featureStatus]="groupData.featureStatus!"
              [toggle]="
                groupData.title === POI_GROUP_TITLE
                  ? layerDataService.placesToggleMap.get(
                      $any(feature).featureConst
                    )!
                  : layerDataService.toggleMap.get($any(feature).featureConst)!
              "
              (changeSelectedYear)="onSelectedYearChange($event)"
              (click)="
                handleInaccessibleFeatureClick(
                  feature.featureConst,
                  feature.featureName || feature.toggleTitle
                )
              "
              [disabled]="!isPlaceAccessible"
            ></app-chart-block>
          }

          @if (isSchoolToggleActive || isOriToggleActive) {
            <app-nearest-poi-panel
              [isSchool]="isSchoolToggleActive"
              [isOri]="isOriToggleActive"
            >
            </app-nearest-poi-panel>
          }
        }
      </div>
    }
  </div>
}
